import { User } from '@access-cms/api-client';
import { splitStreetAddressLines } from '@access-cms/shared-constants';

import { PrimaryStep, SubStep } from '../config/steps';
import { WorkflowFormData } from '../config';
import { USER_CATEGORY } from '../constants';

import { normalizeUsPhoneNumber } from '@/utils/phoneNumber';

// ============================================================================
// User Entity Mapping
// ============================================================================

/**
 * Substeps containing profile fields saved through the User API. Applicant
 * names are application-owned and excluded from User API updates.
 */
export const USER_OWNED_SUBSTEPS = [
  { stepId: PrimaryStep.PERSONAL_DETAILS, substepId: SubStep.BASIC_INFO },
  { stepId: PrimaryStep.PERSONAL_DETAILS, substepId: SubStep.ADDRESS_INFO },
  { stepId: PrimaryStep.PERSONAL_DETAILS, substepId: SubStep.CONTACT_INFO },
] as const;

/**
 * Mapping from User API response fields to form field names.
 * Used when hydrating form data from the server to merge User data
 * into the correct substep shapes.
 *
 * User API field → { substepId, formField } mapping
 *
 * `ssnLast4` is hydrated in userToFormData as a 9-character stored SSN so the
 * existing personal-details input formatter can display it.
 */
export const USER_FIELD_TO_FORM: Record<string, { substepId: string; formField: string }> = {
  addressLine1: { formField: 'addressLine1', substepId: SubStep.ADDRESS_INFO },
  addressLine2: { formField: 'addressLine2', substepId: SubStep.ADDRESS_INFO },
  city: { formField: 'city', substepId: SubStep.ADDRESS_INFO },
  dateOfBirth: { formField: 'dateOfBirth', substepId: SubStep.BASIC_INFO },
  email: { formField: 'registrationEmail', substepId: SubStep.CONTACT_INFO },
  firstName: { formField: 'firstName', substepId: SubStep.BASIC_INFO },
  lastName: { formField: 'lastName', substepId: SubStep.BASIC_INFO },
  middleName: { formField: 'middleName', substepId: SubStep.BASIC_INFO },
  phoneNumber: { formField: 'phoneNumber', substepId: SubStep.CONTACT_INFO },
  postalCode: { formField: 'zipCode', substepId: SubStep.ADDRESS_INFO },
  stateProvince: { formField: 'state', substepId: SubStep.ADDRESS_INFO },
  suffix: { formField: 'suffix', substepId: SubStep.BASIC_INFO },
};

/**
 * Mapping from form field names to User API field names.
 * Used when extracting user-owned data from formData for updateUser.mutate.
 *
 * Form substep/field → User API field mapping
 *
 * Sentinel values:
 * - '_skip': form-only field, kept in applicationData, not sent to any user API
 * - '_ssn_endpoint': SSN field, sent to PATCH /users/{id}/ssn, never stored in applicationData
 * - '_strip': never sent to any API, never stored in applicationData (e.g., ssnConfirm)
 */
export const FORM_FIELD_TO_USER_API: Record<string, Record<string, string>> = {
  [SubStep.BASIC_INFO]: {
    dateOfBirth: 'dateOfBirth',
    firstName: '_skip',
    hasMiddleName: '_skip',
    lastName: '_skip',
    middleName: '_skip',
    otherNames: '_skip',
    ssn: '_ssn_endpoint',
    ssnConfirm: '_strip',
    suffix: 'suffix',
  },
  [SubStep.ADDRESS_INFO]: {
    addressLine1: 'addressLine1',
    addressLine2: 'addressLine2',
    city: 'city',
    state: 'stateProvince',
    workFromHome: '_skip',
    zipCode: 'postalCode',
  },
  [SubStep.CONTACT_INFO]: {
    phoneNumber: 'phoneNumber',
    registrationEmail: 'email',
  },
};

const SSN_DIGITS_REGEX = /\D/g;

/** Strip all non-digit characters, returning only the raw digits. */
export function stripNonDigits(value: string): string {
  return value.replace(SSN_DIGITS_REGEX, '');
}

/**
 * Convert a date string from MM/DD/YYYY (DatePicker format) to
 * YYYY-MM-DD (ISO 8601 date format expected by the API).
 * Returns the original value if it doesn't match the expected format.
 */
export function toApiDateFormat(value: string): string {
  if (!value) return value;
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (match) {
    return `${match[3]}-${match[1]}-${match[2]}`;
  }

  return value;
}

/**
 * Convert a date string from YYYY-MM-DD (API format) to
 * MM/DD/YYYY (format expected by the DatePicker form fields).
 * Returns the original value if it doesn't match the expected format.
 */
export function toFormDateFormat(value: string): string {
  if (!value) return value;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }

  return value;
}

/**
 * Convert User API response object into the form data substep shapes
 * expected by the workflow (basicInfo + personalAddress).
 *
 * Handles:
 * - dateOfBirth: converts YYYY-MM-DD → MM/DD/YYYY for DatePicker
 * - ssnLast4: stored as 9 characters (same shape as a typed SSN) so getFormattedSSN can display it
 * - ssnConfirm: mirrors ssn so confirmation validation passes
 */
export function userToFormData(user: User): Record<string, Record<string, unknown>> {
  const result: Record<string, Record<string, unknown>> = {};

  for (const [userField, mapping] of Object.entries(USER_FIELD_TO_FORM)) {
    let value = (user as Record<string, unknown>)[userField];
    if (value === undefined || value === null) continue;

    if (mapping.formField === 'dateOfBirth' && typeof value === 'string') {
      value = toFormDateFormat(value);
    }
    if (mapping.formField === 'phoneNumber' && typeof value === 'string') {
      value = normalizeUsPhoneNumber(value);
    }

    if (!result[mapping.substepId]) {
      result[mapping.substepId] = {};
    }
    result[mapping.substepId][mapping.formField] = value;
  }

  if (result[SubStep.BASIC_INFO]) {
    result[SubStep.BASIC_INFO].hasMiddleName = Boolean(user.middleName?.trim());
  }

  const addressInfo = result[SubStep.ADDRESS_INFO];
  if (addressInfo && typeof addressInfo.addressLine1 === 'string') {
    const streetLines = splitStreetAddressLines(
      addressInfo.addressLine1,
      typeof addressInfo.addressLine2 === 'string' ? addressInfo.addressLine2 : undefined,
    );
    if (streetLines.addressLine1 !== undefined) {
      addressInfo.addressLine1 = streetLines.addressLine1;
    }
    if (streetLines.addressLine2 !== undefined) {
      addressInfo.addressLine2 = streetLines.addressLine2;
    }
  }

  const last4 = typeof user.ssnLast4 === 'string' ? stripNonDigits(user.ssnLast4) : '';
  if (last4.length === 4) {
    const ssn = last4.slice(-1).padStart(9, '*');
    if (!result[SubStep.BASIC_INFO]) {
      result[SubStep.BASIC_INFO] = {};
    }
    result[SubStep.BASIC_INFO].ssn = ssn;
    result[SubStep.BASIC_INFO].ssnConfirm = ssn;
  }

  return result;
}

/**
 * Extract user-owned fields from formData and map them to the User API body shape.
 * - Fields marked '_skip' are excluded
 * - The 'ssn' field (mapped via '_ssn_endpoint') is extracted separately
 *   and returned alongside the body for the dedicated SSN endpoint
 * - Empty string values are removed (the API treats omitted fields as "no change")
 * - dateOfBirth is converted from MM/DD/YYYY to YYYY-MM-DD
 */
export function extractUserBodyFromFormData(data: WorkflowFormData): {
  userBody: Record<string, unknown>;
  ssn: string | null;
};
export function extractUserBodyFromFormData(
  data: WorkflowFormData,
  options: { includeClearedScalars?: boolean } = {},
): {
  userBody: Record<string, unknown>;
  ssn: string | null;
} {
  const userBody: Record<string, unknown> = {};
  let ssn: string | null = null;

  const canBeClearedWithNull = new Set(['addressLine2', 'suffix']);

  for (const { stepId, substepId } of USER_OWNED_SUBSTEPS) {
    const substepData = data[stepId]?.[substepId] as Record<string, unknown> | undefined;
    if (!substepData) continue;

    const mapping = FORM_FIELD_TO_USER_API[substepId];
    if (!mapping) continue;

    for (const [formField, apiField] of Object.entries(mapping)) {
      if (['_skip', '_strip'].includes(apiField)) continue;

      const value = substepData[formField];

      if (apiField === '_ssn_endpoint') {
        if (typeof value === 'string') {
          const digits = stripNonDigits(value);
          if (digits.length === 9) {
            ssn = digits;
          }
        }
        continue;
      }

      if (!(formField in substepData)) continue;

      if (value === '') {
        if (options.includeClearedScalars && canBeClearedWithNull.has(apiField)) {
          userBody[apiField] = null;
        }
        continue;
      }

      if (apiField === 'dateOfBirth' && typeof value === 'string') {
        userBody[apiField] = toApiDateFormat(value);
      } else if (apiField === 'phoneNumber' && typeof value === 'string') {
        userBody[apiField] = normalizeUsPhoneNumber(value);
      } else {
        userBody[apiField] = value;
      }
    }
  }

  return { ssn, userBody };
}

// ============================================================================
// NOVA Application Data ↔ Form Data Mapping
// ============================================================================
//
// These functions transform between the eNOVA form's nested substep structure
// and the NOVAApplicationDataSchema shape expected by the API.
//
// Form structure:  PrimaryStep → SubStep → { flat field values }
// API structure:    NOVAApplicationDataSchema → { section → { nested fields } }
//
// Key differences handled:
//   - Section names: documentUpload → documents
//   - Field renames: hasMiddleName → noMiddleName, registeredWithSelectiveService →
//     registeredSelectiveService, etc.
//   - Boolean inversions: hasMiddleName (true=has middle) → noMiddleName (true=no middle)
//   - Address nesting: flat form fields → nested { personalAddress, workAddress } objects
//   - List item wrapping: form { id, data: {…} } → API [{ … }]
//   - Immigration: flat form fields → immigrationData array
//   - Passport: flat form fields → nested passport object
//   - Date formats: MM/DD/YYYY ↔ YYYY-MM-DD for API date fields
//   - Legal record field names: correctionDate → date, correctionDetails → details, etc.
// ============================================================================

// ---------------------------------------------------------------------------
// Section name mapping: form PrimaryStep IDs ↔ NOVAApplicationDataSchema keys
// ---------------------------------------------------------------------------

/**
 * Maps form PrimaryStep IDs (UI-internal kebab-case) to
 * NOVAApplicationDataSchema section keys (API camelCase).
 *
 * This is the single source of truth for the UI → API section name
 * translation. Most map 1:1, but `document-upload` → `documents` is
 * the only true rename. Sub-steps do not need a parallel mapping
 * — they are form-internal field-group keys and never appear in
 * the API payload.
 */
export const STEP_TO_API_SECTION: Record<string, string> = {
  [PrimaryStep.PERSONAL_DETAILS]: 'personalDetails',
  [PrimaryStep.CITIZENSHIP_DETAILS]: 'citizenshipDetails',
  [PrimaryStep.CONTRACT_DETAILS]: 'contractDetails',
  [PrimaryStep.OF_306_QUESTIONS]: 'of306Questions',
  [PrimaryStep.DOCUMENT_UPLOAD]: 'documents',
};

// ---------------------------------------------------------------------------
// Value mapping constants
// ---------------------------------------------------------------------------

/**
 * Years in US: form select values ↔ API enum values.
 */
export const YEARS_IN_US_TO_API: Record<string, string> = {
  '1-2 years': 'ONE_TO_TWO',
  '2-3 years': 'TWO_TO_THREE',
  '3-4 years': 'THREE_TO_FOUR',
  '4-5 years': 'FOUR_TO_FIVE',
  '5+ years': 'FIVE_PLUS',
  'Less than 1 year': 'LESS_THAN_1',
};

export const YEARS_IN_US_FROM_API: Record<string, string> = Object.fromEntries(
  Object.entries(YEARS_IN_US_TO_API).map(([label, value]) => [value, label]),
);

/**
 * OF-306 military branch: form select values ↔ API enum values.
 */
export const MILITARY_BRANCH_TO_API: Record<string, string> = {
  'air-force': 'AIR_FORCE',
  army: 'ARMY',
  'coast-guard': 'COAST_GUARD',
  'marine-corps': 'MARINE_CORPS',
  navy: 'NAVY',
  'space-force': 'SPACE_FORCE',
};

export const MILITARY_BRANCH_FROM_API: Record<string, string> = Object.fromEntries(
  Object.entries(MILITARY_BRANCH_TO_API).map(([formValue, apiValue]) => [apiValue, formValue]),
);

/**
 * OF-306 discharge type: form select values ↔ API enum values.
 */
export const DISCHARGE_TYPE_TO_API: Record<string, string> = {
  'bad-conduct': 'BAD_CONDUCT',
  dishonorable: 'DISHONORABLE',
  'entry-level': 'ENTRY_LEVEL',
  general: 'GENERAL',
  honorable: 'HONORABLE',
  'other-than-honorable': 'OTHER_THAN_HONORABLE',
};

export const DISCHARGE_TYPE_FROM_API: Record<string, string> = Object.fromEntries(
  Object.entries(DISCHARGE_TYPE_TO_API).map(([formValue, apiValue]) => [apiValue, formValue]),
);

/**
 * OF-306 retired pay type: form select values ↔ API enum values.
 */
export const RETIRED_PAY_TYPE_TO_API: Record<string, string> = {
  other: 'OTHER',
  pension: 'PENSION',
  'retirement-pay': 'RETIREMENT_PAY',
};

export const RETIRED_PAY_TYPE_FROM_API: Record<string, string> = Object.fromEntries(
  Object.entries(RETIRED_PAY_TYPE_TO_API).map(([formValue, apiValue]) => [apiValue, formValue]),
);

/**
 * OF-306 boolean field mappings: form field name → API field name.
 * Some names differ, some are inversions (hasX → noX / hasY).
 */
const OF306_BOOLEAN_MAP: Record<string, { apiField: string; invert?: boolean }> = {
  bornMaleAfter1959: { apiField: 'bornMaleAfter1959' },
  certificationAccepted: { apiField: 'certificationAgreed' },
  hasCourtMartial: { apiField: 'hasCourtMartial' },
  hasCriminalCorrection: { apiField: 'hasConvictions' },
  hasCurrentViolations: { apiField: 'hasPendingCharges' },
  hasFederalDebt: { apiField: 'hasDelinquentDebt' },
  hasJobDismissal: { apiField: 'wasFiredOrDebarred' },
  hasMilitaryExperience: { apiField: 'hasServedInMilitary' },
  hasRelatives: { apiField: 'hasRelativesInAgency' },
  hasRetirementPay: { apiField: 'hasRetirementIncome' },
  registeredWithSelectiveService: { apiField: 'registeredSelectiveService' },
};

interface ApplicationDataMapOptions {
  /**
   * Include explicit empty arrays for cleared list-style fields so merge-patch
   * updates replace previously saved values.
   */
  includeClearedArrays?: boolean;
  /**
   * Include explicit nulls for cleared scalar fields so merge-patch updates
   * remove previously saved values.
   */
  includeClearedScalars?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Type for a list item stored in the form as { id, data: { … } }. */
interface FormListItem {
  id: string;
  data: Record<string, unknown>;
}

interface ListFieldMapOptions {
  dateFields?: string[];
  fieldMap?: Record<string, string>;
  valueMaps?: Record<string, Record<string, string>>;
}

/**
 * Unwrap form list items [{id, data: {…}}] → plain objects [{…}].
 * Strips the id/data wrapper and maps inner field names if provided.
 */
function unwrapFormList(
  items: FormListItem[] | undefined,
  options: ListFieldMapOptions = {},
): Record<string, unknown>[] {
  if (!items || !Array.isArray(items)) return [];

  return items.map((item) => {
    const raw = item.data ?? {};
    const mapped: Record<string, unknown> = {};
    for (const [formKey, apiValue] of Object.entries(raw)) {
      const apiKey = options.fieldMap?.[formKey] ?? formKey;
      if (apiValue !== undefined && apiValue !== '') {
        if (typeof apiValue === 'string' && options.dateFields?.includes(formKey)) {
          mapped[apiKey] = toApiDateFormat(apiValue);
        } else if (typeof apiValue === 'string' && options.valueMaps?.[formKey]) {
          mapped[apiKey] = options.valueMaps[formKey][apiValue] ?? apiValue;
        } else {
          mapped[apiKey] = apiValue;
        }
      }
    }

    return mapped;
  });
}

/**
 * Wrap API array items [{…}] → form list items [{id, data: {…}}].
 * Reverse-maps inner field names if provided.
 */
function wrapApiList(items: Record<string, unknown>[] | undefined, options: ListFieldMapOptions = {}): FormListItem[] {
  if (!items || !Array.isArray(items)) return [];

  return items.map((item, idx) => {
    const mapped: Record<string, unknown> = {};
    const reverseMap = options.fieldMap
      ? Object.fromEntries(Object.entries(options.fieldMap).map(([formKey, apiKey]) => [apiKey, formKey]))
      : undefined;
    for (const [apiKey, apiValue] of Object.entries(item)) {
      const formKey = reverseMap?.[apiKey] ?? apiKey;
      if (typeof apiValue === 'string' && options.dateFields?.includes(formKey)) {
        mapped[formKey] = toFormDateFormat(apiValue);
      } else if (typeof apiValue === 'string' && options.valueMaps?.[formKey]) {
        mapped[formKey] = options.valueMaps[formKey][apiValue] ?? apiValue;
      } else {
        mapped[formKey] = apiValue;
      }
    }

    return { data: mapped, id: `item-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 9)}` };
  });
}

function isCompleteRetiredPayDraft(draft: Record<string, unknown> | undefined): boolean {
  if (!draft) return false;
  if (typeof draft.payType !== 'string' || !draft.payType) return false;
  if (draft.payType === 'other' && (typeof draft.otherPayType !== 'string' || !draft.otherPayType)) return false;
  if (typeof draft.stillReceivingPay !== 'boolean') return false;

  return draft.stillReceivingPay === true
    ? typeof draft.receiptStartDate === 'string' && !!draft.receiptStartDate
    : typeof draft.receiptEndDate === 'string' && !!draft.receiptEndDate;
}

/**
 * Convert a boolean value, optionally inverting it.
 */
function mapBool(value: unknown, invert?: boolean): unknown {
  if (typeof value !== 'boolean') return value;

  return invert ? !value : value;
}

/**
 * Pick specified keys from an object, returning a new object with only those keys.
 * Skips keys whose values are undefined, null, or empty string.
 */
function pickNonEmpty(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== '') {
      result[key] = value;
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Form → API (NOVAApplicationDataSchema)
// ---------------------------------------------------------------------------

/**
 * Transform workflow form data into the NOVAApplicationDataSchema shape
 * expected by the PATCH /applications/:id endpoint.
 *
 * This function:
 *   - Restructures nested substep data into API section format
 *   - Renames fields that differ between form and API
 *   - Inverts booleans where the API uses the opposite sense
 *   - Nests flat address fields into { personalAddress, workAddress } objects
 *   - Unwraps form list items {id, data: {…}} → plain {…} objects
 *   - Converts date strings from MM/DD/YYYY → YYYY-MM-DD where the API expects dates
 *   - Excludes fields owned by the User entity (dateOfBirth, suffix, etc.)
 *   - Excludes SSN and SSN-confirm (handled by separate endpoints)
 *   - Excludes contract entity fields (company, contractNumber, laborCategory)
 *
 * Section keys are produced via STEP_TO_API_SECTION mapping (kebab UI → camel API).
 */
export function formDataToApplicationData(
  formData: WorkflowFormData,
  options: ApplicationDataMapOptions = {},
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  result[STEP_TO_API_SECTION[PrimaryStep.PERSONAL_DETAILS]] = buildPersonalDetails(formData, options);
  result[STEP_TO_API_SECTION[PrimaryStep.CITIZENSHIP_DETAILS]] = buildCitizenshipDetails(formData, options);
  result[STEP_TO_API_SECTION[PrimaryStep.CONTRACT_DETAILS]] = buildContractDetails(formData);
  result[STEP_TO_API_SECTION[PrimaryStep.OF_306_QUESTIONS]] = buildOf306Questions(formData, options);
  result[STEP_TO_API_SECTION[PrimaryStep.DOCUMENT_UPLOAD]] = buildDocuments(formData, options);

  // Remove empty sections (no useful data)
  for (const key of Object.keys(result)) {
    const section = result[key] as Record<string, unknown>;
    if (section && typeof section === 'object' && Object.keys(section).length === 0) {
      delete result[key];
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Form → API: Section builders
// ---------------------------------------------------------------------------

function buildPersonalDetails(formData: WorkflowFormData, options: ApplicationDataMapOptions): Record<string, unknown> {
  const section: Record<string, unknown> = {};

  // --- BASIC_INFO (suffix, dateOfBirth → User API;
  //     ssn → SSN endpoint; ssnConfirm → stripped) ---
  const basicInfo = (formData[PrimaryStep.PERSONAL_DETAILS]?.[SubStep.BASIC_INFO] ?? {}) as Record<string, unknown>;

  for (const field of ['firstName', 'lastName', 'middleName'] as const) {
    if (typeof basicInfo[field] === 'string') {
      section[field] = field === 'middleName' && basicInfo.hasMiddleName === false ? '' : basicInfo[field].trim();
    }
  }

  // hasMiddleName (true = has middle name) → noMiddleName (true = no middle name)
  if (basicInfo.hasMiddleName !== undefined && basicInfo.hasMiddleName !== null) {
    section.noMiddleName = !basicInfo.hasMiddleName;
  }

  // otherNames (text) → otherNamesUsed (array of OtherNameSchema-shaped objects)
  if (typeof basicInfo.otherNames === 'string' && (basicInfo.otherNames as string).trim()) {
    section.otherNamesUsed = [{ lastName: (basicInfo.otherNames as string).trim() }];
  } else if (options.includeClearedArrays && typeof basicInfo.otherNames === 'string') {
    section.otherNamesUsed = [];
  }

  // --- ADDRESS_INFO (personalAddress fields also go to User API, but we
  //     include them in the application data as a snapshot per NOVAApplicationDataSchema) ---
  const addressInfo = (formData[PrimaryStep.PERSONAL_DETAILS]?.[SubStep.ADDRESS_INFO] ?? {}) as Record<string, unknown>;

  const personalAddress: Record<string, unknown> = {};
  if (addressInfo.addressLine1) personalAddress.streetLine1 = addressInfo.addressLine1;
  if (addressInfo.addressLine2) personalAddress.streetLine2 = addressInfo.addressLine2;
  if (addressInfo.city) personalAddress.city = addressInfo.city;
  if (addressInfo.state) personalAddress.state = addressInfo.state;
  if (addressInfo.zipCode) personalAddress.zipCode = addressInfo.zipCode;
  if (Object.keys(personalAddress).length > 0) {
    section.personalAddress = personalAddress;
  }

  // workFromHome stays at the top level of personalDetails
  if (addressInfo.workFromHome !== undefined && addressInfo.workFromHome !== null) {
    section.workFromHome = addressInfo.workFromHome;
  }

  // --- CONTACT_INFO ---
  const contactInfo = (formData[PrimaryStep.PERSONAL_DETAILS]?.[SubStep.CONTACT_INFO] ?? {}) as Record<string, unknown>;

  if (contactInfo.phoneNumber) {
    section.personalPhoneNumber = contactInfo.phoneNumber;
  }
  if (contactInfo.userCategory) section.userCategory = USER_CATEGORY.VALUE;

  return section;
}

function buildCitizenshipDetails(
  formData: WorkflowFormData,
  options: ApplicationDataMapOptions,
): Record<string, unknown> {
  const section: Record<string, unknown> = {};

  // --- CITIZENSHIP_STATUS ---
  const status = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] ?? {}) as Record<
    string,
    unknown
  >;
  if (status.isUSCitizen !== undefined && status.isUSCitizen !== null) {
    section.isUsCitizen = status.isUSCitizen;
  }
  const isUsCitizen = status.isUSCitizen === true;
  const isNonUsCitizen = status.isUSCitizen === false;

  // --- BIRTH_INFORMATION ---
  const birth = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.BIRTH_INFORMATION] ?? {}) as Record<
    string,
    unknown
  >;
  if (birth.birthCity) section.birthCity = birth.birthCity;
  if (birth.birthCountry) section.birthCountry = birth.birthCountry;
  if (birth.birthCountryCode) section.birthCountryCode = birth.birthCountryCode;
  if (birth.birthState) section.birthState = birth.birthState;
  if (birth.sex) section.sex = birth.sex;

  // --- RESIDENCY_INFORMATION ---
  const residency = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] ?? {}) as Record<
    string,
    unknown
  >;
  if (isNonUsCitizen) {
    if (residency.citizenshipCountry) section.countryOfCitizenship = residency.citizenshipCountry;
    if (residency.citizenshipCountryCode) section.countryOfCitizenshipCode = residency.citizenshipCountryCode;
    if (residency.isPermanentResident !== undefined && residency.isPermanentResident !== null) {
      section.isPermanentResident = residency.isPermanentResident;
    }
  }
  const isPermanentResident = status.isUSCitizen === false && residency.isPermanentResident === true;

  if (options.includeClearedScalars && isUsCitizen) {
    section.countryOfCitizenship = null;
    section.countryOfCitizenshipCode = null;
    section.isPermanentResident = null;
  }

  // --- US_ENTRY_INFORMATION ---
  const entry = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.US_ENTRY_INFORMATION] ?? {}) as Record<
    string,
    unknown
  >;
  if (isNonUsCitizen && !isPermanentResident && entry.yearsInUS) {
    section.yearsInUSPast5Years = YEARS_IN_US_TO_API[entry.yearsInUS as string] ?? entry.yearsInUS;
  }
  // firstEntryDate and entryVisaType stored as passthrough since the schema uses .passthrough()
  if (isNonUsCitizen && !isPermanentResident && entry.firstEntryDate) {
    section.firstEntryDate = toApiDateFormat(entry.firstEntryDate as string);
  }
  if (isNonUsCitizen && !isPermanentResident && entry.entryVisaType) section.entryVisaType = entry.entryVisaType;
  if (options.includeClearedScalars && (isUsCitizen || isPermanentResident)) {
    section.entryVisaType = null;
    section.firstEntryDate = null;
    section.yearsInUSPast5Years = null;
  }

  // --- IMMIGRATION_DOCUMENT ---
  const immigration = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.IMMIGRATION_DOCUMENT] ?? {}) as Record<
    string,
    unknown
  >;
  if (isNonUsCitizen && !isPermanentResident && (immigration.immigrationDate || immigration.visaNumber)) {
    const record: Record<string, unknown> = {};
    record.documentType = 'VISA_NUMBER';
    if (immigration.immigrationDate) {
      record.immigrationDate = toApiDateFormat(immigration.immigrationDate as string);
    }
    if (immigration.visaNumber) record.visaNumber = immigration.visaNumber;
    section.immigrationData = [record];
  } else if (options.includeClearedScalars && (isUsCitizen || isPermanentResident)) {
    section.immigrationData = null;
  }

  // --- PASSPORT_INFORMATION ---
  const passport = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.PASSPORT_INFORMATION] ?? {}) as Record<
    string,
    unknown
  >;
  if (
    isNonUsCitizen &&
    (passport.passportNumber ||
      passport.passportType ||
      passport.passportIssueDate ||
      passport.passportExpirationDate ||
      passport.passportIssuanceCountry ||
      passport.passportIssuanceCountryCode)
  ) {
    const passportObj: Record<string, unknown> = {};
    if (passport.passportNumber) passportObj.number = passport.passportNumber;
    if (passport.passportType) passportObj.type = passport.passportType;
    if (passport.passportIssueDate) passportObj.issueDate = passport.passportIssueDate;
    if (passport.passportExpirationDate) passportObj.expirationDate = passport.passportExpirationDate;
    if (passport.passportIssuanceCountry) passportObj.issuanceCountry = passport.passportIssuanceCountry;
    if (passport.passportIssuanceCountryCode) passportObj.issuanceCountryCode = passport.passportIssuanceCountryCode;
    section.passport = passportObj;
  } else if (options.includeClearedScalars && isUsCitizen) {
    section.passport = null;
  }

  return section;
}

function buildContractDetails(formData: WorkflowFormData): Record<string, unknown> {
  const section: Record<string, unknown> = {};

  // --- CONTRACT_INFO ---
  const contractInfo = (formData[PrimaryStep.CONTRACT_DETAILS]?.[SubStep.CONTRACT_INFO] ?? {}) as Record<
    string,
    unknown
  >;
  if (contractInfo.requestJustification) section.accessJustification = contractInfo.requestJustification;
  if (contractInfo.cmsRegion) section.cmsRegionFacility = contractInfo.cmsRegion;
  // company, contractNumber, laborCategory are pulled from related entities, not stored here

  // --- WORK_CONTACT_INFO ---
  const workContact = (formData[PrimaryStep.CONTRACT_DETAILS]?.[SubStep.WORK_CONTACT_INFO] ?? {}) as Record<
    string,
    unknown
  >;

  const workAddress: Record<string, unknown> = {};
  if (workContact.workAddressLine1) workAddress.streetLine1 = workContact.workAddressLine1;
  if (workContact.workAddressLine2) workAddress.streetLine2 = workContact.workAddressLine2;
  if (workContact.workAddressCity) workAddress.city = workContact.workAddressCity;
  if (workContact.workAddressState) workAddress.state = workContact.workAddressState;
  if (workContact.workAddressZipCode) workAddress.zipCode = workContact.workAddressZipCode;
  if (Object.keys(workAddress).length > 0) {
    section.workAddress = workAddress;
  }

  if (workContact.workEmail) section.workEmail = workContact.workEmail;
  if (workContact.workExtension) section.workExtension = workContact.workExtension;
  if (typeof workContact.workPhoneNumber === 'string' && workContact.workPhoneNumber) {
    section.workPhoneNumber = normalizeUsPhoneNumber(workContact.workPhoneNumber);
  }

  return section;
}

function buildOf306Questions(formData: WorkflowFormData, options: ApplicationDataMapOptions): Record<string, unknown> {
  const section: Record<string, unknown> = {};

  // --- SELECTIVE_SERVICE ---
  const selective = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.SELECTIVE_SERVICE] ?? {}) as Record<
    string,
    unknown
  >;
  if (selective.bornMaleAfter1959 !== undefined && selective.bornMaleAfter1959 !== null) {
    section.bornMaleAfter1959 = selective.bornMaleAfter1959;
  }
  if (selective.registeredWithSelectiveService !== undefined && selective.registeredWithSelectiveService !== null) {
    section.registeredSelectiveService = selective.registeredWithSelectiveService;
  }
  if (selective.selectiveServiceExplanation) {
    section.selectiveServiceExplanation = selective.selectiveServiceExplanation;
  } else if (
    options.includeClearedScalars &&
    (selective.bornMaleAfter1959 === false || selective.registeredWithSelectiveService === true)
  ) {
    section.selectiveServiceExplanation = null;
  }
  if (options.includeClearedScalars && selective.bornMaleAfter1959 === false) {
    section.registeredSelectiveService = null;
  }

  // --- MILITARY_EXPERIENCE ---
  const military = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.MILITARY_EXPERIENCE] ?? {}) as Record<
    string,
    unknown
  >;
  if (military.hasMilitaryExperience !== undefined && military.hasMilitaryExperience !== null) {
    section.hasServedInMilitary = military.hasMilitaryExperience;
  }
  const militaryList = military.militaryExperiences as FormListItem[] | undefined;
  if (militaryList && militaryList.length > 0) {
    section.militaryService = unwrapFormList(militaryList, {
      dateFields: ['dateServedFrom', 'dateServedTo'],
      valueMaps: {
        branch: MILITARY_BRANCH_TO_API,
        dischargeType: DISCHARGE_TYPE_TO_API,
      },
    });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(militaryList) &&
    militaryList.length === 0 &&
    (military.hasMilitaryExperience === true || military.hasMilitaryExperience === false)
  ) {
    section.militaryService = [];
  }

  // --- CRIMINAL_CORRECTION → convictions ---
  const criminal = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.CRIMINAL_CORRECTION] ?? {}) as Record<
    string,
    unknown
  >;
  if (criminal.hasCriminalCorrection !== undefined && criminal.hasCriminalCorrection !== null) {
    section.hasConvictions = criminal.hasCriminalCorrection;
  }
  const criminalList = criminal.criminalCorrections as FormListItem[] | undefined;
  if (criminalList && criminalList.length > 0) {
    section.convictions = unwrapFormList(criminalList, {
      dateFields: ['correctionDate'],
      fieldMap: { correctionDate: 'date', correctionDetails: 'details' },
    });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(criminalList) &&
    criminalList.length === 0 &&
    (criminal.hasCriminalCorrection === true || criminal.hasCriminalCorrection === false)
  ) {
    section.convictions = [];
  }

  // --- COURT_MARTIAL ---
  const martial = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.COURT_MARTIAL] ?? {}) as Record<string, unknown>;
  if (martial.hasCourtMartial !== undefined && martial.hasCourtMartial !== null) {
    section.hasCourtMartial = martial.hasCourtMartial;
  }
  const martialList = martial.courtMartials as FormListItem[] | undefined;
  if (martialList && martialList.length > 0) {
    section.courtMartials = unwrapFormList(martialList, {
      dateFields: ['courtMartialDate'],
      fieldMap: {
        courtMartialDate: 'date',
        courtMartialDetails: 'details',
      },
    });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(martialList) &&
    martialList.length === 0 &&
    (martial.hasCourtMartial === true || martial.hasCourtMartial === false)
  ) {
    section.courtMartials = [];
  }

  // --- CURRENT_VIOLATIONS → pendingCharges ---
  const violations = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.CURRENT_VIOLATIONS] ?? {}) as Record<
    string,
    unknown
  >;
  if (violations.hasCurrentViolations !== undefined && violations.hasCurrentViolations !== null) {
    section.hasPendingCharges = violations.hasCurrentViolations;
  }
  const violationsList = violations.violations as FormListItem[] | undefined;
  if (violationsList && violationsList.length > 0) {
    section.pendingCharges = unwrapFormList(violationsList, {
      dateFields: ['violationDate'],
      fieldMap: { violationDate: 'date', violationDetails: 'details' },
    });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(violationsList) &&
    violationsList.length === 0 &&
    (violations.hasCurrentViolations === true || violations.hasCurrentViolations === false)
  ) {
    section.pendingCharges = [];
  }

  // --- JOB_DISMISSAL → wasFiredOrDebarred ---
  const dismissal = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.JOB_DISMISSAL] ?? {}) as Record<string, unknown>;
  if (dismissal.hasJobDismissal !== undefined && dismissal.hasJobDismissal !== null) {
    section.wasFiredOrDebarred = dismissal.hasJobDismissal;
  }
  const dismissalList = dismissal.dismissals as FormListItem[] | undefined;
  if (dismissalList && dismissalList.length > 0) {
    section.dismissals = unwrapFormList(dismissalList, {
      dateFields: ['dismissalDate'],
      fieldMap: { dismissalDate: 'date', dismissalDetails: 'details' },
    });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(dismissalList) &&
    dismissalList.length === 0 &&
    (dismissal.hasJobDismissal === true || dismissal.hasJobDismissal === false)
  ) {
    section.dismissals = [];
  }

  // --- FEDERAL_DEBT → hasDelinquentDebt ---
  const debt = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.FEDERAL_DEBT] ?? {}) as Record<string, unknown>;
  if (debt.hasFederalDebt !== undefined && debt.hasFederalDebt !== null) {
    section.hasDelinquentDebt = debt.hasFederalDebt;
  }
  const debtList = debt.federalDebts as FormListItem[] | undefined;
  if (debtList && debtList.length > 0) {
    section.federalDebts = unwrapFormList(debtList, { fieldMap: { debtDetails: 'details' } });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(debtList) &&
    debtList.length === 0 &&
    (debt.hasFederalDebt === true || debt.hasFederalDebt === false)
  ) {
    section.federalDebts = [];
  }

  // --- RELATIVES → hasRelativesInAgency ---
  const relatives = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.RELATIVES] ?? {}) as Record<string, unknown>;
  if (relatives.hasRelatives !== undefined && relatives.hasRelatives !== null) {
    section.hasRelativesInAgency = relatives.hasRelatives;
  }
  const relativesList = relatives.relatives as FormListItem[] | undefined;
  if (relativesList && relativesList.length > 0) {
    section.relatives = unwrapFormList(relativesList, { fieldMap: { relativeDetails: 'details' } });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(relativesList) &&
    relativesList.length === 0 &&
    (relatives.hasRelatives === true || relatives.hasRelatives === false)
  ) {
    section.relatives = [];
  }

  // --- RETIREMENT_PAY → hasRetirementIncome ---
  const retirement = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.RETIREMENT_PAY] ?? {}) as Record<
    string,
    unknown
  >;
  if (retirement.hasRetirementPay !== undefined && retirement.hasRetirementPay !== null) {
    section.hasRetirementIncome = retirement.hasRetirementPay;
    section.hasPriorFederalEmployment = retirement.hasRetirementPay;
  }
  const retiredPayList = retirement.retiredPays as FormListItem[] | undefined;
  const retiredPayItems = [...(retiredPayList ?? [])];
  const draftRetiredPay = retirement.draftRetiredPay as Record<string, unknown> | undefined;
  if (draftRetiredPay && isCompleteRetiredPayDraft(draftRetiredPay)) {
    retiredPayItems.push({ data: draftRetiredPay, id: 'draft-retired-pay' });
  }

  if (retiredPayItems.length > 0) {
    section.retiredPayDetails = unwrapFormList(retiredPayItems, {
      dateFields: ['receiptStartDate', 'receiptEndDate'],
      valueMaps: { payType: RETIRED_PAY_TYPE_TO_API },
    });
  } else if (
    options.includeClearedArrays &&
    Array.isArray(retiredPayList) &&
    retiredPayList.length === 0 &&
    (retirement.hasRetirementPay === true || retirement.hasRetirementPay === false)
  ) {
    section.retiredPayDetails = [];
  }

  // --- CERTIFICATION → certificationAgreed ---
  const cert = (formData[PrimaryStep.OF_306_QUESTIONS]?.[SubStep.CERTIFICATION] ?? {}) as Record<string, unknown>;
  if (cert.certificationAccepted !== undefined && cert.certificationAccepted !== null) {
    section.certificationAgreed = cert.certificationAccepted;
  }

  return section;
}

function buildDocuments(formData: WorkflowFormData, options: ApplicationDataMapOptions): Record<string, unknown> {
  const section: Record<string, unknown> = {};

  const upload = (formData[PrimaryStep.DOCUMENT_UPLOAD]?.[SubStep.UPLOAD_DOCUMENTS] ?? {}) as Record<string, unknown>;
  const citizenship = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.CITIZENSHIP_STATUS] ?? {}) as Record<
    string,
    unknown
  >;
  const residency = (formData[PrimaryStep.CITIZENSHIP_DETAILS]?.[SubStep.RESIDENCY_INFORMATION] ?? {}) as Record<
    string,
    unknown
  >;
  const isNonUsCitizen = citizenship.isUSCitizen === false;
  const isPermanentResident = isNonUsCitizen && residency.isPermanentResident === true;

  // Document fields: arrays in form, mapped to their API shape
  // Single-document fields (resume, passport, etc.) take first item from form array
  const docsMap: Record<string, { formKey: string; isSingle: boolean }> = {
    legalDocuments: { formKey: 'legalDocuments', isSingle: false },
    otherDocuments: { formKey: 'otherDocuments', isSingle: false },
    passport: { formKey: 'passport', isSingle: true },
    pivTrainingCertificate: { formKey: 'pivTrainingCertificate', isSingle: true },
    residencyDocument: { formKey: 'residencyDocument', isSingle: true },
    residencySupportingDocuments: { formKey: 'residencySupportingDocuments', isSingle: false },
    resume: { formKey: 'resume', isSingle: true },
    usVisaDocument: { formKey: 'usVisaDocument', isSingle: true },
  };

  const toDocumentRef = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object') return null;

    const file = value as Record<string, unknown>;
    const documentId =
      typeof file.documentId === 'string'
        ? file.documentId
        : file.uploadStatus === 'associated' && typeof file.id === 'string'
        ? file.id
        : undefined;
    const filename = file.filename ?? file.name;
    const size = file.sizeBytes ?? file.size;

    if (typeof documentId !== 'string' || typeof filename !== 'string') {
      return null;
    }

    const ref: Record<string, unknown> = {
      filename,
      id: documentId,
    };

    if (typeof file.mimeType === 'string') ref.mimeType = file.mimeType;
    if (typeof file.s3Key === 'string') ref.s3Key = file.s3Key;
    if (typeof size === 'number') ref.size = size;
    if (typeof file.uploadedAt === 'string') ref.uploadedAt = file.uploadedAt;

    return ref;
  };

  for (const [apiKey, { formKey, isSingle }] of Object.entries(docsMap)) {
    const isInapplicableVisa = apiKey === 'usVisaDocument' && (!isNonUsCitizen || isPermanentResident);
    const isInapplicableResidency = apiKey === 'residencyDocument' && !isPermanentResident;
    const isInapplicablePassport = apiKey === 'passport' && !isNonUsCitizen;
    const isInapplicableResidencySupport = apiKey === 'residencySupportingDocuments' && !isNonUsCitizen;

    if (isInapplicableVisa || isInapplicableResidency || isInapplicablePassport || isInapplicableResidencySupport) {
      if (isSingle && options.includeClearedScalars) section[apiKey] = null;
      if (!isSingle && options.includeClearedArrays) section[apiKey] = [];
      continue;
    }

    const value = upload[formKey];
    if (!value) continue;

    if (isSingle) {
      // Single document: take first item from form array
      const arr = value as unknown[];
      if (Array.isArray(arr) && arr.length > 0) {
        const ref = toDocumentRef(arr[0]);
        if (ref) section[apiKey] = ref;
      } else if (options.includeClearedScalars && Array.isArray(arr) && arr.length === 0) {
        section[apiKey] = null;
      }
    } else {
      // Array document: pass through
      if (Array.isArray(value) && value.length > 0) {
        const refs = value.map(toDocumentRef).filter(Boolean);
        if (refs.length > 0) section[apiKey] = refs;
      } else if (options.includeClearedArrays && Array.isArray(value) && value.length === 0) {
        section[apiKey] = [];
      }
    }
  }

  return section;
}

// ---------------------------------------------------------------------------
// API → Form (NOVAApplicationDataSchema → WorkflowFormData)
// ---------------------------------------------------------------------------

/**
 * Transform NOVAApplicationDataSchema-shaped API response data back into
 * the workflow form data structure (PrimaryStep → SubStep → { fields }).
 *
 * This is the inverse of formDataToApplicationData(), used when hydrating
 * the form from a saved application loaded from the server.
 *
 * Handles:
 *   - Section name mapping (documents → documentUpload)
 *   - Field renames (noMiddleName → hasMiddleName, etc.)
 *   - Boolean inversions (noMiddleName → hasMiddleName)
 *   - Nested address objects → flat form fields
 *   - API arrays → form {id, data: {…}} list items
 *   - Immigration record unwrapping
 *   - Passport object → flat form fields
 *   - Date format conversion (YYYY-MM-DD → MM/DD/YYYY where applicable)
 *
 * @param apiData - The applicationData object from the API response
 * @returns Partial form data structured as { stepId: { substepId: { … } } }
 */
export function applicationDataToFormData(apiData: Record<string, unknown>): Partial<WorkflowFormData> {
  const result: Partial<WorkflowFormData> = {};

  // ===== personalDetails → BASIC_INFO, ADDRESS_INFO, CONTACT_INFO =====
  const personalDetails = (apiData[STEP_TO_API_SECTION[PrimaryStep.PERSONAL_DETAILS]] ?? {}) as Record<string, unknown>;
  if (Object.keys(personalDetails).length > 0) {
    // Saved names override the profile defaults, including a cleared middle name.
    const savedNames: Record<string, unknown> = {};
    for (const field of ['firstName', 'lastName', 'middleName'] as const) {
      if (typeof personalDetails[field] === 'string') savedNames[field] = personalDetails[field];
    }
    if (typeof personalDetails.middleName === 'string') {
      savedNames.hasMiddleName = Boolean(personalDetails.middleName.trim());
    }
    if (Object.keys(savedNames).length > 0) {
      result[PrimaryStep.PERSONAL_DETAILS] = { [SubStep.BASIC_INFO]: savedNames };
    }

    // BASIC_INFO
    if (personalDetails.noMiddleName !== undefined) {
      const noMiddleName = personalDetails.noMiddleName === true;
      if (!result[PrimaryStep.PERSONAL_DETAILS]) result[PrimaryStep.PERSONAL_DETAILS] = {} as Record<string, unknown>;
      const basicInfo = ((result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.BASIC_INFO] ??
        {}) as Record<string, unknown>;
      (result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.BASIC_INFO] = {
        ...basicInfo,
        hasMiddleName: !noMiddleName,
        ...(noMiddleName ? { middleName: '' } : {}),
      };
    }

    if (personalDetails.otherNamesUsed) {
      const arr = personalDetails.otherNamesUsed as Record<string, unknown>[];
      if (arr.length > 0) {
        if (!result[PrimaryStep.PERSONAL_DETAILS]) result[PrimaryStep.PERSONAL_DETAILS] = {} as Record<string, unknown>;
        const basicInfo = ((result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.BASIC_INFO] ??
          {}) as Record<string, unknown>;
        // Take lastName from first entry as the simplified form value
        const lastName = arr[0].lastName as string | undefined;
        (result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.BASIC_INFO] = {
          ...basicInfo,
          otherNames: lastName ?? '',
        };
      }
    }

    // ADDRESS_INFO
    const personalAddress = (personalDetails.personalAddress ?? {}) as Record<string, unknown>;
    const addressFields = pickNonEmpty(personalAddress, ['streetLine1', 'streetLine2', 'city', 'state', 'zipCode']);
    const hasAddressData = Object.keys(addressFields).length > 0;

    if (hasAddressData || personalDetails.workFromHome !== undefined) {
      if (!result[PrimaryStep.PERSONAL_DETAILS]) result[PrimaryStep.PERSONAL_DETAILS] = {} as Record<string, unknown>;
      const addressInfo = ((result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.ADDRESS_INFO] ??
        {}) as Record<string, unknown>;
      const merged: Record<string, unknown> = { ...addressInfo };
      if (addressFields.streetLine1 !== undefined) merged.addressLine1 = addressFields.streetLine1;
      if (addressFields.streetLine2 !== undefined) merged.addressLine2 = addressFields.streetLine2;
      if (addressFields.city !== undefined) merged.city = addressFields.city;
      if (addressFields.state !== undefined) merged.state = addressFields.state;
      if (addressFields.zipCode !== undefined) merged.zipCode = addressFields.zipCode;
      if (personalDetails.workFromHome !== undefined) merged.workFromHome = personalDetails.workFromHome;
      (result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.ADDRESS_INFO] = merged;
    }

    // CONTACT_INFO
    if (personalDetails.personalPhoneNumber || personalDetails.userCategory) {
      if (!result[PrimaryStep.PERSONAL_DETAILS]) result[PrimaryStep.PERSONAL_DETAILS] = {} as Record<string, unknown>;
      const contactInfo = ((result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.CONTACT_INFO] ??
        {}) as Record<string, unknown>;
      (result[PrimaryStep.PERSONAL_DETAILS] as Record<string, unknown>)[SubStep.CONTACT_INFO] = {
        ...contactInfo,
        // phoneNumber: personalDetails.personalPhoneNumber,
        userCategory: USER_CATEGORY.VALUE,
      };
    }
  }

  // ===== citizenshipDetails ====
  const citizenshipDetails = (apiData[STEP_TO_API_SECTION[PrimaryStep.CITIZENSHIP_DETAILS]] ?? {}) as Record<
    string,
    unknown
  >;
  if (Object.keys(citizenshipDetails).length > 0) {
    if (!result[PrimaryStep.CITIZENSHIP_DETAILS])
      result[PrimaryStep.CITIZENSHIP_DETAILS] = {} as Record<string, unknown>;

    // CITIZENSHIP_STATUS
    const statusFields: Record<string, unknown> = {};
    if (citizenshipDetails.isUsCitizen !== undefined) statusFields.isUSCitizen = citizenshipDetails.isUsCitizen;
    if (Object.keys(statusFields).length > 0) {
      (result[PrimaryStep.CITIZENSHIP_DETAILS] as Record<string, unknown>)[SubStep.CITIZENSHIP_STATUS] = statusFields;
    }

    // BIRTH_INFORMATION
    const birthFields: Record<string, unknown> = {};
    if (citizenshipDetails.birthCity) birthFields.birthCity = citizenshipDetails.birthCity;
    if (citizenshipDetails.birthCountry) birthFields.birthCountry = citizenshipDetails.birthCountry;
    if (citizenshipDetails.birthCountryCode) birthFields.birthCountryCode = citizenshipDetails.birthCountryCode;
    if (citizenshipDetails.birthState) birthFields.birthState = citizenshipDetails.birthState;
    if (citizenshipDetails.sex) birthFields.sex = citizenshipDetails.sex;
    if (Object.keys(birthFields).length > 0) {
      (result[PrimaryStep.CITIZENSHIP_DETAILS] as Record<string, unknown>)[SubStep.BIRTH_INFORMATION] = birthFields;
    }

    // RESIDENCY_INFORMATION
    const residencyFields: Record<string, unknown> = {};
    if (citizenshipDetails.countryOfCitizenship)
      residencyFields.citizenshipCountry = citizenshipDetails.countryOfCitizenship;
    if (citizenshipDetails.countryOfCitizenshipCode)
      residencyFields.citizenshipCountryCode = citizenshipDetails.countryOfCitizenshipCode;
    if (citizenshipDetails.isPermanentResident !== undefined)
      residencyFields.isPermanentResident = citizenshipDetails.isPermanentResident;
    if (Object.keys(residencyFields).length > 0) {
      (result[PrimaryStep.CITIZENSHIP_DETAILS] as Record<string, unknown>)[SubStep.RESIDENCY_INFORMATION] =
        residencyFields;
    }

    // US_ENTRY_INFORMATION
    const entryFields: Record<string, unknown> = {};
    if (citizenshipDetails.yearsInUSPast5Years) {
      entryFields.yearsInUS =
        YEARS_IN_US_FROM_API[citizenshipDetails.yearsInUSPast5Years as string] ??
        citizenshipDetails.yearsInUSPast5Years;
    }
    if (citizenshipDetails.firstEntryDate) {
      entryFields.firstEntryDate = toFormDateFormat(citizenshipDetails.firstEntryDate as string);
    }
    if (citizenshipDetails.entryVisaType) entryFields.entryVisaType = citizenshipDetails.entryVisaType;
    if (Object.keys(entryFields).length > 0) {
      (result[PrimaryStep.CITIZENSHIP_DETAILS] as Record<string, unknown>)[SubStep.US_ENTRY_INFORMATION] = entryFields;
    }

    // IMMIGRATION_DOCUMENT
    const immigrationData = citizenshipDetails.immigrationData as Record<string, unknown>[] | undefined;
    if (immigrationData && immigrationData.length > 0) {
      const record = immigrationData[0]; // Form currently supports a single immigration record
      const immigrationFields: Record<string, unknown> = {};
      immigrationFields.documentType = 'Visa Number';
      if (record.immigrationDate !== undefined) {
        immigrationFields.immigrationDate = toFormDateFormat(record.immigrationDate as string);
      }
      if (record.visaNumber !== undefined) immigrationFields.visaNumber = record.visaNumber;
      if (Object.keys(immigrationFields).length > 0) {
        (result[PrimaryStep.CITIZENSHIP_DETAILS] as Record<string, unknown>)[SubStep.IMMIGRATION_DOCUMENT] =
          immigrationFields;
      }
    }

    // PASSPORT_INFORMATION
    const passport = (citizenshipDetails.passport ?? {}) as Record<string, unknown>;
    const passportFields: Record<string, unknown> = {};
    if (passport.number) passportFields.passportNumber = passport.number;
    if (passport.type) passportFields.passportType = passport.type;
    if (passport.issueDate) passportFields.passportIssueDate = passport.issueDate;
    if (passport.expirationDate) passportFields.passportExpirationDate = passport.expirationDate;
    if (passport.issuanceCountry) passportFields.passportIssuanceCountry = passport.issuanceCountry;
    if (passport.issuanceCountryCode) passportFields.passportIssuanceCountryCode = passport.issuanceCountryCode;
    if (Object.keys(passportFields).length > 0) {
      (result[PrimaryStep.CITIZENSHIP_DETAILS] as Record<string, unknown>)[SubStep.PASSPORT_INFORMATION] =
        passportFields;
    }
  }

  // ===== contractDetails =====
  const contractDetails = (apiData[STEP_TO_API_SECTION[PrimaryStep.CONTRACT_DETAILS]] ?? {}) as Record<string, unknown>;
  if (Object.keys(contractDetails).length > 0) {
    if (!result[PrimaryStep.CONTRACT_DETAILS]) result[PrimaryStep.CONTRACT_DETAILS] = {} as Record<string, unknown>;

    // CONTRACT_INFO
    const contractInfoFields: Record<string, unknown> = {};
    if (contractDetails.accessJustification)
      contractInfoFields.requestJustification = contractDetails.accessJustification;
    if (contractDetails.cmsRegionFacility) contractInfoFields.cmsRegion = contractDetails.cmsRegionFacility;
    if (Object.keys(contractInfoFields).length > 0) {
      (result[PrimaryStep.CONTRACT_DETAILS] as Record<string, unknown>)[SubStep.CONTRACT_INFO] = contractInfoFields;
    }

    // WORK_CONTACT_INFO
    const workAddress = (contractDetails.workAddress ?? {}) as Record<string, unknown>;
    const workContactFields: Record<string, unknown> = {};
    if (workAddress.streetLine1) workContactFields.workAddressLine1 = workAddress.streetLine1;
    if (workAddress.streetLine2) workContactFields.workAddressLine2 = workAddress.streetLine2;
    if (workAddress.city) workContactFields.workAddressCity = workAddress.city;
    if (workAddress.state) workContactFields.workAddressState = workAddress.state;
    if (workAddress.zipCode) workContactFields.workAddressZipCode = workAddress.zipCode;
    if (contractDetails.workEmail) workContactFields.workEmail = contractDetails.workEmail;
    if (contractDetails.workExtension) workContactFields.workExtension = contractDetails.workExtension;
    if (typeof contractDetails.workPhoneNumber === 'string' && contractDetails.workPhoneNumber) {
      workContactFields.workPhoneNumber = normalizeUsPhoneNumber(contractDetails.workPhoneNumber);
    }
    if (Object.keys(workContactFields).length > 0) {
      (result[PrimaryStep.CONTRACT_DETAILS] as Record<string, unknown>)[SubStep.WORK_CONTACT_INFO] = workContactFields;
    }
  }

  // ===== of306Questions =====
  const of306 = (apiData[STEP_TO_API_SECTION[PrimaryStep.OF_306_QUESTIONS]] ?? {}) as Record<string, unknown>;
  if (Object.keys(of306).length > 0) {
    if (!result[PrimaryStep.OF_306_QUESTIONS]) result[PrimaryStep.OF_306_QUESTIONS] = {} as Record<string, unknown>;

    const of306Step = result[PrimaryStep.OF_306_QUESTIONS] as Record<string, unknown>;

    // Reverse-map all OF-306 boolean fields
    for (const [formField, mapping] of Object.entries(OF306_BOOLEAN_MAP)) {
      const apiValue = of306[mapping.apiField];
      if (apiValue !== undefined && apiValue !== null) {
        const substepData = (of306Step[getSubstepForOf306Field(formField)] ?? {}) as Record<string, unknown>;
        of306Step[getSubstepForOf306Field(formField)] = {
          ...substepData,
          [formField]: mapBool(apiValue, mapping.invert),
        };
      }
    }

    // SELECTIVE_SERVICE extra fields
    const selectiveSubstep = (of306Step[SubStep.SELECTIVE_SERVICE] ?? {}) as Record<string, unknown>;
    if (of306.selectiveServiceExplanation !== undefined && of306.selectiveServiceExplanation !== null) {
      selectiveSubstep.selectiveServiceExplanation = of306.selectiveServiceExplanation;
    }
    if (of306.selectiveServiceNumber !== undefined && of306.selectiveServiceNumber !== null) {
      selectiveSubstep.selectiveServiceNumber = of306.selectiveServiceNumber;
    }
    if (Object.keys(selectiveSubstep).length > 0) {
      of306Step[SubStep.SELECTIVE_SERVICE] = selectiveSubstep;
    }

    // MILITARY_EXPERIENCE list
    const militaryList = of306.militaryService as Record<string, unknown>[] | undefined;
    if (militaryList && militaryList.length > 0) {
      const militarySubstep = (of306Step[SubStep.MILITARY_EXPERIENCE] ?? {}) as Record<string, unknown>;
      militarySubstep.militaryExperiences = wrapApiList(militaryList, {
        dateFields: ['dateServedFrom', 'dateServedTo'],
        valueMaps: {
          branch: MILITARY_BRANCH_FROM_API,
          dischargeType: DISCHARGE_TYPE_FROM_API,
        },
      });
      of306Step[SubStep.MILITARY_EXPERIENCE] = militarySubstep;
    }

    // CRIMINAL_CORRECTION list → convictions
    const convictionsList = of306.convictions as Record<string, unknown>[] | undefined;
    if (convictionsList && convictionsList.length > 0) {
      const criminalSubstep = (of306Step[SubStep.CRIMINAL_CORRECTION] ?? {}) as Record<string, unknown>;
      criminalSubstep.criminalCorrections = wrapApiList(convictionsList, {
        dateFields: ['correctionDate'],
        fieldMap: {
          correctionDate: 'date',
          correctionDetails: 'details',
        },
      });
      of306Step[SubStep.CRIMINAL_CORRECTION] = criminalSubstep;
    }

    // COURT_MARTIAL list
    const courtMartialsList = of306.courtMartials as Record<string, unknown>[] | undefined;
    if (courtMartialsList && courtMartialsList.length > 0) {
      const courtMartialSubstep = (of306Step[SubStep.COURT_MARTIAL] ?? {}) as Record<string, unknown>;
      courtMartialSubstep.courtMartials = wrapApiList(courtMartialsList, {
        dateFields: ['courtMartialDate'],
        fieldMap: {
          courtMartialDate: 'date',
          courtMartialDetails: 'details',
        },
      });
      of306Step[SubStep.COURT_MARTIAL] = courtMartialSubstep;
    }

    // CURRENT_VIOLATIONS list → pendingCharges
    const pendingChargesList = of306.pendingCharges as Record<string, unknown>[] | undefined;
    if (pendingChargesList && pendingChargesList.length > 0) {
      const violationsSubstep = (of306Step[SubStep.CURRENT_VIOLATIONS] ?? {}) as Record<string, unknown>;
      violationsSubstep.violations = wrapApiList(pendingChargesList, {
        dateFields: ['violationDate'],
        fieldMap: {
          violationDate: 'date',
          violationDetails: 'details',
        },
      });
      of306Step[SubStep.CURRENT_VIOLATIONS] = violationsSubstep;
    }

    // JOB_DISMISSAL list → dismissals
    const dismissalsList = of306.dismissals as Record<string, unknown>[] | undefined;
    if (dismissalsList && dismissalsList.length > 0) {
      const dismissalSubstep = (of306Step[SubStep.JOB_DISMISSAL] ?? {}) as Record<string, unknown>;
      dismissalSubstep.dismissals = wrapApiList(dismissalsList, {
        dateFields: ['dismissalDate'],
        fieldMap: {
          dismissalDate: 'date',
          dismissalDetails: 'details',
        },
      });
      of306Step[SubStep.JOB_DISMISSAL] = dismissalSubstep;
    }

    // FEDERAL_DEBT list
    const federalDebtsList = of306.federalDebts as Record<string, unknown>[] | undefined;
    if (federalDebtsList && federalDebtsList.length > 0) {
      const debtSubstep = (of306Step[SubStep.FEDERAL_DEBT] ?? {}) as Record<string, unknown>;
      debtSubstep.federalDebts = wrapApiList(federalDebtsList, { fieldMap: { debtDetails: 'details' } });
      of306Step[SubStep.FEDERAL_DEBT] = debtSubstep;
    }

    // RELATIVES list
    const relativesList = of306.relatives as Record<string, unknown>[] | undefined;
    if (relativesList && relativesList.length > 0) {
      const relativesSubstep = (of306Step[SubStep.RELATIVES] ?? {}) as Record<string, unknown>;
      relativesSubstep.relatives = wrapApiList(relativesList, { fieldMap: { relativeDetails: 'details' } });
      of306Step[SubStep.RELATIVES] = relativesSubstep;
    }

    // RETIREMENT_PAY list
    const retiredPayDetailsList = of306.retiredPayDetails as Record<string, unknown>[] | undefined;
    if (retiredPayDetailsList && retiredPayDetailsList.length > 0) {
      const retirementSubstep = (of306Step[SubStep.RETIREMENT_PAY] ?? {}) as Record<string, unknown>;
      retirementSubstep.retiredPays = wrapApiList(retiredPayDetailsList, {
        dateFields: ['receiptStartDate', 'receiptEndDate'],
        valueMaps: { payType: RETIRED_PAY_TYPE_FROM_API },
      });
      of306Step[SubStep.RETIREMENT_PAY] = retirementSubstep;
    }
  }

  // ===== documents → DOCUMENT_UPLOAD =====
  const documents = (apiData[STEP_TO_API_SECTION[PrimaryStep.DOCUMENT_UPLOAD]] ?? {}) as Record<string, unknown>;
  if (Object.keys(documents).length > 0) {
    const uploadFields: Record<string, unknown> = {};
    const toUploadedFile = (value: unknown): Record<string, unknown> | null => {
      if (!value || typeof value !== 'object') return null;

      const doc = value as Record<string, unknown>;
      if (typeof doc.filename !== 'string') return null;

      const id = typeof doc.id === 'string' ? doc.id : doc.filename;

      return {
        documentId: typeof doc.id === 'string' ? doc.id : undefined,
        id,
        mimeType: typeof doc.mimeType === 'string' ? doc.mimeType : undefined,
        name: doc.filename,
        size: typeof doc.size === 'number' ? doc.size : 0,
        uploadedAt: typeof doc.uploadedAt === 'string' ? doc.uploadedAt : undefined,
        uploadStatus: typeof doc.id === 'string' ? 'associated' : undefined,
      };
    };

    // Single document fields: wrap API object in form array
    const singleDocKeys = ['passport', 'pivTrainingCertificate', 'residencyDocument', 'resume', 'usVisaDocument'];
    for (const key of singleDocKeys) {
      if (documents[key] !== undefined) {
        const uploadedFile = toUploadedFile(documents[key]);
        if (uploadedFile) uploadFields[key] = [uploadedFile];
      }
    }

    // Array document fields: pass through
    const arrayDocKeys = ['legalDocuments', 'otherDocuments', 'residencySupportingDocuments'];
    for (const key of arrayDocKeys) {
      if (documents[key] !== undefined) {
        const values = documents[key];
        uploadFields[key] = Array.isArray(values) ? values.map(toUploadedFile).filter(Boolean) : [];
      }
    }

    if (Object.keys(uploadFields).length > 0) {
      result[PrimaryStep.DOCUMENT_UPLOAD] = { [SubStep.UPLOAD_DOCUMENTS]: uploadFields };
    }
  }

  return result;
}

/**
 * Determines which OF-306 substep a boolean field belongs to.
 * Used by the reverse mapping to place API values in the correct form substep.
 */
function getSubstepForOf306Field(formField: string): string {
  const fieldToSubstep: Record<string, string> = {
    bornMaleAfter1959: SubStep.SELECTIVE_SERVICE,
    certificationAccepted: SubStep.CERTIFICATION,
    hasCourtMartial: SubStep.COURT_MARTIAL,
    hasCriminalCorrection: SubStep.CRIMINAL_CORRECTION,
    hasCurrentViolations: SubStep.CURRENT_VIOLATIONS,
    hasFederalDebt: SubStep.FEDERAL_DEBT,
    hasJobDismissal: SubStep.JOB_DISMISSAL,
    hasMilitaryExperience: SubStep.MILITARY_EXPERIENCE,
    hasRelatives: SubStep.RELATIVES,
    hasRetirementPay: SubStep.RETIREMENT_PAY,
    registeredWithSelectiveService: SubStep.SELECTIVE_SERVICE,
  };

  return fieldToSubstep[formField] ?? SubStep.SELECTIVE_SERVICE;
}

// ---------------------------------------------------------------------------
// extractApplicationDataFromFormData (updated to use NOVAApplicationDataSchema)
// ---------------------------------------------------------------------------

/**
 * Build the applicationData payload for the NOVAApplicationDataSchema format.
 *
 * This replaces the previous approach which sent form-shaped data with user
 * fields stripped. It now produces the proper NOVAApplicationDataSchema shape:
 *   - Section names use STEP_TO_API_SECTION mapping (kebab UI → camel API)
 *   - Field names match the API schema (noMiddleName, hasServedInMilitary, etc.)
 *   - Structural transforms are applied (nested addresses, unwrapped lists, etc.)
 *   - User-owned fields are excluded (they go to the User API separately)
 *
 * @param data - The full workflow form data
 * @returns NOVAApplicationDataSchema-shaped object ready for the API
 */
export function extractApplicationDataFromFormData(
  data: WorkflowFormData,
  options: ApplicationDataMapOptions = {},
): Record<string, unknown> {
  return formDataToApplicationData(data, options);
}

// ---------------------------------------------------------------------------
// Related entity hydration (contract, labor category)
// ---------------------------------------------------------------------------
//
// Contract and labor category data comes from related API entities, not from
// the applicationData payload. These fields are displayed in the form as
// read-only values and must NOT be sent back to the API in the applicationData
// payload (they are managed by the contract/labor-category entities).
//
// The form's ContractInformationPage renders:
//   - contractNumber  (read-only, from contract entity)
//   - company         (read-only, from contract entity)
//   - laborCategory   (read-only, from labor category entity)
//   - cmsRegion       (editable, stored as cmsRegionFacility in contractDetails)
//   - requestJustification (editable, stored as accessJustification in contractDetails)
// ---------------------------------------------------------------------------

/** Type for the contract object returned by the /me endpoint. */
export type ContractEntity = {
  companyName?: string | null;
  contractNumber?: string | null;
  [key: string]: unknown;
};

/** Type for the labor category object returned by the /me endpoint. */
export type LaborCategoryEntity = {
  description?: string | null;
  id: string;
  name?: string | null;
  [key: string]: unknown;
};

/**
 * Maps contract and labor category data into the form's contractInfo substep.
 *
 * These fields populate the read-only portion of ContractInformationPage.
 * They are NOT included in the applicationData API payload on save —
 * they come from separate contract/labor-category entities.
 */
export function relatedEntitiesToFormData(
  contract: ContractEntity | null,
  laborCategory: LaborCategoryEntity | null,
): Record<string, Record<string, unknown>> {
  const contractInfo: Record<string, unknown> = {};

  if (contract) {
    if (contract.contractNumber) contractInfo.contractNumber = contract.contractNumber;
    if (contract.companyName) contractInfo.company = contract.companyName;
  }

  if (laborCategory) {
    if (laborCategory.categoryName) contractInfo.laborCategory = laborCategory.categoryName;
  }

  if (Object.keys(contractInfo).length === 0) {
    return {};
  }

  return {
    [PrimaryStep.CONTRACT_DETAILS]: {
      [SubStep.CONTRACT_INFO]: contractInfo,
    },
  };
}

/**
 * Strips contract entity fields from the contractInfo substep before sending
 * to the API. These read-only fields (contractNumber, company, laborCategory)
 * belong to separate entities and must not be persisted in applicationData.
 */

export function stripContractEntityFields(applicationData: Record<string, unknown>): Record<string, unknown> {
  const contractSectionKey = STEP_TO_API_SECTION[PrimaryStep.CONTRACT_DETAILS];
  const contractDetails = applicationData[contractSectionKey] as Record<string, unknown> | undefined;
  if (!contractDetails) return applicationData;

  const cleaned = structuredClone(applicationData);
  const cleanedContractDetails = cleaned[contractSectionKey] as Record<string, unknown>;
  delete cleanedContractDetails.contractNumber;
  delete cleanedContractDetails.company;
  delete cleanedContractDetails.laborCategory;

  return cleaned;
}

/**
 * Canonical applicationData payload for API persistence and attestation document hashing.
 * Must match saveProgress and server-side getDocumentHashPayload input.
 */
export function toPersistedApplicationData(data: WorkflowFormData): Record<string, unknown> {
  return stripContractEntityFields(
    extractApplicationDataFromFormData(data, {
      includeClearedArrays: true,
      includeClearedScalars: true,
    }),
  );
}
